import { useCallback, useContext, useMemo } from 'react';
import CurrentUserContext from '../../../../../contexts/CurrentUserContext';
import ImpersonatorContext from '../../../../../contexts/ImpersonatorContext';
import RolesContext from '../../../../../contexts/RolesContext';
import PERMISSIONS from '../../../../../permissionDefinitions';

const useAppSideNav = (app?: V3.App) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { currentPermissions } = useContext(RolesContext);

  const isAppAdmin = useMemo(() => {
    if (!app) {
      return false;
    }
    const userId = impersonatorId ? impersonatorId : currentUserId;
    if (app.adminUserIds.includes(userId)) {
      return true;
    }

    if (
      currentPermissions.includes(
        PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_ADMIN_ONLY,
      )
    ) {
      return true;
    }

    if (
      currentPermissions.includes(PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_PRIVATE)
    ) {
      return true;
    }

    return false;
  }, [currentPermissions, currentUserId, impersonatorId, app]);

  const sections = useMemo((): V3.Navigation.SideSection[] => {
    if (!app) {
      return [];
    }

    if (isAppAdmin) {
      return app.navigationSettings.sections;
    }

    return app.navigationSettings.sections.filter((s) => !s.isAppAdminOnly);
  }, [isAppAdmin, app]);

  const getItems = useCallback(
    (section: V3.Navigation.SideSection): V3.Navigation.Item[] => {
      if (isAppAdmin) {
        return section.items;
      }

      return section.items.filter((i) => {
        if (i.type === 'Bonus Period Picker') {
          return true;
        }

        return !i.isAppAdminOnly;
      });
    },
    [isAppAdmin],
  );

  return {
    sections,
    getItems,
    isAppAdmin,
  };
};

export default useAppSideNav;
